<script setup lang="ts">
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import { computed, onMounted, reactive, watch } from 'vue';
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectLabel,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import type { ProfileConfig } from '@/types/edit/ProfileConfig.ts';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Button } from '@/components/ui/button';
import { VariableIcon, ClipboardCopyIcon } from 'lucide-vue-next';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { type VariableConfig } from '@/types/edit/VariableConfig.ts';
import { VariableConfigService } from '@/services/config/VariableConfigService.ts';
import type { PromptConfig } from '@/types/edit/PromptConfig.ts';
import {
    TagsInput,
    TagsInputInput,
    TagsInputItem,
    TagsInputItemDelete,
    TagsInputItemText,
} from '@/components/ui/tags-input';
import { Textarea } from '@/components/ui/textarea';
import { VariableService } from '@/services/VariableService.ts';
import copy from 'copy-to-clipboard';

const props = defineProps<{
    promptConfigs: Record<PromptConfig['id'], PromptConfig>;
    profileConfigId: ProfileConfig['id'];
}>();

interface ComponentState {
    isVisible: boolean;
    variableConfigs: Record<VariableConfig['id'], VariableConfig>;
    selectedVariableConfigId: VariableConfig['id'] | undefined;
}

const state: ComponentState = reactive({
    isVisible: false,
    variableConfigs: {},
    selectedVariableConfigId: undefined,
});

/* region Event handlers */
const setSelectedVariableConfigId = (id: VariableConfig['id']) => {
    state.selectedVariableConfigId = id;
};

const close = () => {
    state.isVisible = false;
};
/* endregion Event handlers */

/* region Other */
const loadVariableConfigs = () => {
    state.variableConfigs = VariableConfigService.getVariableConfigs(
        props.promptConfigs,
        props.profileConfigId,
        ['DEFAULT', 'CUSTOM', 'PROMPT_OUTPUT', 'PROMPT_USER_INPUT'],
    );
};

/* endregion Other */

/* region Computed */
const selectedVariableConfig = computed((): VariableConfig | undefined => {
    if (!state.selectedVariableConfigId) {
        return undefined;
    }

    return state.variableConfigs[state.selectedVariableConfigId];
});
/* region Computed */

/* region Lifecycle */
watch(
    () => [props.promptConfigs, props.profileConfigId],
    async () => {
        loadVariableConfigs();
    },
    { deep: true },
);

onMounted(async () => {
    loadVariableConfigs();
});
/* endregion Lifecycle */
</script>

<template>
    <Dialog v-model:open="state.isVisible">
        <DialogTrigger as-child>
            <Button variant="secondary">
                <VariableIcon />
                <span class="hidden sm:inline">Available variables</span>
            </Button>
        </DialogTrigger>
        <DialogContent class="sm:max-w-[525px]">
            <DialogHeader>
                <DialogTitle>Available variables</DialogTitle>
                <DialogDescription>
                    You can reference these variables in your prompts with the "$" symbol.<br /><br />
                    You can edit and create variables from the setting menu (Settings -> All
                    settings -> Variables).
                </DialogDescription>
            </DialogHeader>

            <div class="flex w-full flex-col items-center gap-y-2">
                <div class="flex w-full items-center gap-x-2">
                    <Select
                        :model-value="state.selectedVariableConfigId"
                        @update:model-value="setSelectedVariableConfigId"
                    >
                        <SelectTrigger class="focus:ring-0 focus-visible:ring-0">
                            <SelectValue placeholder="Select a variable" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectGroup>
                                <SelectLabel>Default</SelectLabel>
                                <template
                                    v-if="
                                        Object.values(state.variableConfigs).filter(
                                            (v) => v.type === 'DEFAULT',
                                        ).length > 0
                                    "
                                >
                                    <SelectItem
                                        v-for="config in Object.values(
                                            state.variableConfigs,
                                        ).filter((v) => v.type === 'DEFAULT')"
                                        :key="config.key"
                                        :value="config.id"
                                    >
                                        {{ config.key }}
                                    </SelectItem>
                                </template>
                                <SelectItem v-else disabled value="0"> None found </SelectItem>
                            </SelectGroup>
                            <SelectGroup>
                                <SelectLabel>Custom</SelectLabel>
                                <template
                                    v-if="
                                        Object.values(state.variableConfigs).filter(
                                            (v) => v.type === 'CUSTOM',
                                        ).length > 0
                                    "
                                >
                                    <SelectItem
                                        v-for="config in Object.values(
                                            state.variableConfigs,
                                        ).filter((v) => v.type === 'CUSTOM')"
                                        :key="config.key"
                                        :value="config.id"
                                    >
                                        {{ config.key }}
                                    </SelectItem>
                                </template>
                                <SelectItem v-else disabled value="0"> None found </SelectItem>
                            </SelectGroup>
                            <SelectGroup>
                                <SelectLabel>Output</SelectLabel>
                                <template
                                    v-if="
                                        Object.values(state.variableConfigs).filter(
                                            (v) => v.type === 'PROMPT_OUTPUT',
                                        ).length > 0
                                    "
                                >
                                    <SelectItem
                                        v-for="config in Object.values(
                                            state.variableConfigs,
                                        ).filter((v) => v.type === 'PROMPT_OUTPUT')"
                                        :key="config.key"
                                        :value="config.id"
                                    >
                                        {{ config.key }}
                                    </SelectItem>
                                </template>
                                <SelectItem v-else disabled value="0"> None found </SelectItem>
                            </SelectGroup>
                            <SelectGroup>
                                <SelectLabel>Choice</SelectLabel>
                                <template
                                    v-if="
                                        Object.values(state.variableConfigs).filter(
                                            (v) => v.type === 'PROMPT_USER_INPUT',
                                        ).length > 0
                                    "
                                >
                                    <SelectItem
                                        v-for="config in Object.values(
                                            state.variableConfigs,
                                        ).filter((v) => v.type === 'PROMPT_USER_INPUT')"
                                        :key="config.key"
                                        :value="config.id"
                                    >
                                        {{ config.key }}
                                    </SelectItem>
                                </template>
                                <SelectItem v-else disabled value="0"> None found </SelectItem>
                            </SelectGroup>
                        </SelectContent>
                    </Select>
                    <TooltipProvider :delay-duration="150">
                        <Tooltip>
                            <TooltipTrigger>
                                <Button
                                    type="button"
                                    variant="secondary"
                                    size="icon"
                                    class="size-10"
                                    @click="
                                        selectedVariableConfig
                                            ? copy(`$${selectedVariableConfig.key}`)
                                            : undefined
                                    "
                                    :disabled="!selectedVariableConfig"
                                >
                                    <ClipboardCopyIcon />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent> Copy variable to clipboard </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                </div>
            </div>
            <template v-if="selectedVariableConfig">
                <Separator class="mb-4" />

                <!-- Value string input -->
                <template
                    v-if="
                        selectedVariableConfig.type === 'DEFAULT' ||
                        selectedVariableConfig.type === 'CUSTOM'
                    "
                >
                    <Label for="variable-value"> Variable value </Label>
                    <Textarea
                        v-if="selectedVariableConfig.valueType === 'string'"
                        :model-value="
                            selectedVariableConfig.type === 'DEFAULT'
                                ? (VariableService.getDefaultVariableValue(
                                      selectedVariableConfig.id,
                                  ) as string)
                                : (selectedVariableConfig.value as string)
                        "
                        id="variable-value"
                        :maxlength="500"
                        class="h-30"
                        disabled
                    />

                    <!-- Value array input -->
                    <TagsInput
                        v-if="selectedVariableConfig.valueType === 'array'"
                        :model-value="
                            selectedVariableConfig.type === 'DEFAULT'
                                ? (VariableService.getDefaultVariableValue(
                                      selectedVariableConfig.id,
                                  ) as string[])
                                : (selectedVariableConfig.value as string[])
                        "
                        id="variable-value"
                        class="disabled"
                        disabled
                    >
                        <TagsInputItem
                            v-for="item in selectedVariableConfig.value"
                            :key="item"
                            :value="item"
                        >
                            <TagsInputItemText />
                            <TagsInputItemDelete />
                        </TagsInputItem>

                        <TagsInputInput placeholder='Values (type and press "Enter" to add)' />
                    </TagsInput>
                </template>
                <template
                    v-if="
                        selectedVariableConfig.type === 'PROMPT_OUTPUT' ||
                        selectedVariableConfig.type === 'PROMPT_USER_INPUT'
                    "
                >
                    <Label for="variable-description" class="mb-2">Variable description</Label>
                    <Textarea
                        :model-value="selectedVariableConfig.description"
                        id="variable-description "
                        disabled
                    />
                </template>
            </template>

            <DialogFooter>
                <Button type="button" @click="close"> Close </Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>
